import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Paper,
  Stack,
  useTheme,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Print as PrintIcon,
  Description as DocumentIcon,
} from "@mui/icons-material";

export default function HtmlViewer({
  title,
  htmlContent,
  updatedAt,
  onEdit,
  showActions = true,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [copiedText, setCopiedText] = useState(false);

  const handleCopyText = () => {
    if (!htmlContent) return;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const plainText = tempDiv.textContent || tempDiv.innerText || "";
    navigator.clipboard.writeText(plainText);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>${title || "Meeting Notes"}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 40px; color: #1e293b; line-height: 1.6; }
            h1, h2, h3, h4 { color: #0f172a; margin-top: 1.2em; margin-bottom: 0.6em; }
            table { width: 100%; border-collapse: collapse; margin: 16px 0; }
            th, td { border: 1px solid #cbd5e1; padding: 10px; text-align: left; }
            th { background-color: #f1f5f9; font-weight: 600; }
            ul, ol { padding-left: 24px; margin: 12px 0; }
            blockquote { border-left: 4px solid #f59e0b; padding-left: 12px; font-style: italic; color: #475569; }
            hr { border: 0; border-top: 1px solid #e2e8f0; margin: 20px 0; }
          </style>
        </head>
        <body>
          <h1>${title || "Meeting Notes"}</h1>
          ${updatedAt ? `<p style="font-size: 13px; color: #64748b;">Last Updated: ${updatedAt}</p>` : ""}
          <hr/>
          ${htmlContent || "<p>No content prepared.</p>"}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (!htmlContent || !htmlContent.trim()) {
    return (
      <Box
        sx={{
          p: 5,
          textAlign: "center",
          borderRadius: "16px",
          border: "1px dashed",
          borderColor: isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1",
          bgcolor: isDark ? "rgba(11,20,55,0.3)" : "#f8fafc",
        }}
      >
        <DocumentIcon sx={{ fontSize: 48, color: isDark ? "#4a5568" : "#cbd5e1", mb: 1.5 }} />
        <Typography variant="body1" sx={{ fontWeight: 700, color: isDark ? "#ffffff" : "#1b2559" }}>
          No notes prepared yet
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? "#a3aed0" : "#707eae", mt: 0.5, mb: 3 }}>
          Prepare meeting minutes, agenda, decisions, or action items.
        </Typography>
        {onEdit && (
          <Button
            variant="contained"
            size="small"
            onClick={onEdit}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 700,
              px: 2.5,
              py: 0.8,
            }}
          >
            Prepare Notes
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1px solid",
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
        bgcolor: isDark ? "#111c44" : "#ffffff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER ACTIONS */}
      {showActions && (
        <Box
          sx={{
            p: 2,
            px: 2.5,
            bgcolor: isDark ? "#0b1437" : "#f8fafc",
            borderBottom: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <DocumentIcon sx={{ fontSize: 20, color: "#8b5cf6" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? "#ffffff" : "#1b2559" }}>
              {title || "Meeting Notes"}
            </Typography>
            {updatedAt && (
              <Chip
                label={`Updated: ${new Date(updatedAt).toLocaleDateString()}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.72rem", height: 22, color: "text.secondary" }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip title={copiedText ? "Notes Copied!" : "Copy Notes"}>
              <IconButton size="small" onClick={handleCopyText} sx={{ color: isDark ? "#a3aed0" : "#707eae" }}>
                {copiedText ? <CheckIcon fontSize="small" sx={{ color: "#10b981" }} /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Print / PDF">
              <IconButton size="small" onClick={handlePrint} sx={{ color: isDark ? "#a3aed0" : "#707eae" }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {onEdit && (
              <Button
                size="small"
                variant="outlined"
                onClick={onEdit}
                sx={{
                  ml: 1,
                  textTransform: "none",
                  borderRadius: "8px",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  borderColor: isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1",
                  color: isDark ? "#ffffff" : "#1b2559",
                  "&:hover": { borderColor: "#f59e0b" },
                }}
              >
                Edit Notes
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* CONTENT VIEW */}
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            color: isDark ? "#cbd5e1" : "#334155",
            fontSize: "0.95rem",
            lineHeight: 1.7,
            "& h1": { fontSize: "1.6rem", fontWeight: 800, mb: 1.5, color: isDark ? "#ffffff" : "#1b2559" },
            "& h2": { fontSize: "1.3rem", fontWeight: 800, mt: 2.5, mb: 1, color: isDark ? "#ffffff" : "#1b2559" },
            "& h3": { fontSize: "1.1rem", fontWeight: 700, mt: 2, mb: 0.75, color: isDark ? "#ffffff" : "#1b2559" },
            "& p": { lineHeight: 1.7, mb: 1.5 },
            "& ul, & ol": { pl: 3, mb: 2 },
            "& li": { mb: 0.5 },
            "& table": {
              width: "100%",
              borderCollapse: "collapse",
              my: 2.5,
              borderRadius: "10px",
              overflow: "hidden",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
            },
            "& th": {
              bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
              p: 1.5,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#cbd5e1"}`,
              textAlign: "left",
              fontWeight: 700,
              color: isDark ? "#ffffff" : "#1b2559",
            },
            "& td": {
              p: 1.5,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
            },
            "& tr:hover td": {
              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
            },
            "& blockquote": {
              borderLeft: "4px solid #f59e0b",
              pl: 2,
              py: 0.5,
              fontStyle: "italic",
              my: 2,
              color: isDark ? "#a3aed0" : "#64748b",
              bgcolor: isDark ? "rgba(245, 158, 11, 0.05)" : "#fffbeb",
              borderRadius: "0 8px 8px 0",
            },
            "& hr": {
              border: 0,
              borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
              my: 2.5,
            },
            "& a": {
              color: "#3b82f6",
              textDecoration: "underline",
            },
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </Box>
    </Paper>
  );
}
